import { jobListQuerySchema, type JobListQuery, type Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { toListParams } from '../../../../../shared/policies/list-params';
import { useListQuery } from '../../../../../shared/hooks/use-list-query';
import { listJobs, type JobListItem } from '../../../jobs.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type JobsState = { status: 'loading' } | { status: 'loaded'; page: Page<JobListItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: JobListQuery) => toListParams(query).toString();

function toState(page: Page<JobListItem>, query: JobListQuery): JobsState {
  return { status: 'loaded', page, queryKey: keyOf(query) };
}

function toErrorState(err: unknown): JobsState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of jobs the URL's list query asks for (`useListQuery`), re-fetched whenever that query changes. */
export function useJobList() {
  const list = useListQuery(jobListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<JobsState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    listJobs(query)
      .then((page) => {
        if (!cancelled) setState(toState(page, query));
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  return { state, list, isRefreshing };
}
